/** @jsxImportSource react */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase';
import { BackgroundParticles } from '../shared/BackgroundParticles';
import { LogoInsaite } from '../shared/LogoInsaite';
import { userService } from '@/services/user.service';
import { getRoleFromToken, clearSessionData } from '@/utils/tokenValidation';
import { getTokenForPath } from '@/utils/authToken';
import { PAYMENTS_ENABLED } from '@/config/features';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import { Breadcrumbs } from '../shared/Breadcrumbs';
import { AppModal } from '../shared/AppModal';
import { OnboardingGuide } from '../shared/OnboardingGuide';
import { ImpersonationBanner } from '../shared/ImpersonationBanner';
import { useNotifications } from '@/contexts/NotificationContext';
import { getNotificationIcon } from '../shared/NotificationIcons';
import './RecruiterLayout.css';
import './RecruiterModals.css';

function getInitials(name: string): string {
  if (!name) return '?';
  const names = name.trim().split(/\s+/);
  const initials = names.map((n) => n[0]).join('');
  return initials.slice(0, 2).toUpperCase();
}

const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 56;


// ── Static SVG icons (hoisted outside component to avoid re-creation) ──
const collapseIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 20, height: 20 },
  React.createElement('path', { d: 'M15 18L9 12L15 6', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' })
);
const expandIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 20, height: 20 },
  React.createElement('path', { d: 'M3 12H21M3 6H21M3 18H21', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' })
);
const dashboardIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('rect', { x: '3', y: '3', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '14', y: '3', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '3', y: '14', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '14', y: '14', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' })
);
const jdUploadIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', { d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('path', { d: 'M14 2V8H20', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' }),
  React.createElement('path', { d: 'M8 13H16', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' }),
  React.createElement('path', { d: 'M8 17H16', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' })
);
const premiumIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', { d: 'M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' })
);
const settingsIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('circle', { cx: '12', cy: '12', r: '3', stroke: 'currentColor', strokeWidth: '2', fill: 'none' }),
  React.createElement('path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z', stroke: 'currentColor', strokeWidth: '2', fill: 'none' })
);
const logoutIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
  React.createElement('path', { d: 'M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' }),
  React.createElement('path', { d: 'M16 17L21 12L16 7', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' }),
  React.createElement('path', { d: 'M21 12H9', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' })
);
const globeIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 20, height: 20 },
  React.createElement('circle', { cx: '12', cy: '12', r: '10', stroke: 'currentColor', strokeWidth: '1.5' }),
  React.createElement('ellipse', { cx: '12', cy: '12', rx: '10', ry: '4', stroke: 'currentColor', strokeWidth: '1.5', transform: 'rotate(90 12 12)' }),
  React.createElement('path', { d: 'M2 12H22', stroke: 'currentColor', strokeWidth: '1.5' })
);
const bellIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 22, height: 22 },
  React.createElement('path', { d: 'M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }),
  React.createElement('path', { d: 'M13.73 21a2 2 0 0 1-3.46 0', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' })
);

const adminPanelIconSvg = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 22, height: 22 },
  React.createElement('path', { d: 'M12 3l8 4v5c0 4.5-3.2 7.9-8 9-4.8-1.1-8-4.5-8-9V7l8-4z', stroke: 'currentColor', strokeWidth: '1.5', strokeLinejoin: 'round' }),
  React.createElement('path', { d: 'M9 12l2 2 4-4', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' })
);

export function RecruiterLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const esAdmin = useMemo(() => {
    const token = getTokenForPath(location.pathname);
    return !!token && getRoleFromToken(token) === 'admin';
  }, [location.pathname]);
  const { t, theme, lang, setTheme, setLang } = useThemeLanguage();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [pulsingPath, setPulsingPath] = useState<string | null>(null);
  const [user, setUser] = useState<{ displayName?: string | null; email?: string | null; photoURL?: string | null } | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('recruiter_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('recruiter_sidebar_collapsed', String(next)); } catch { }
      return next;
    });
  }, []);

  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  useEffect(() => {
    userService.getProfile().then((profile) => {
      if (profile) setIsPremium(profile.is_premium);
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u ? { displayName: u.displayName, email: u.email, photoURL: u.photoURL } : null);
    });
    return () => unsubscribe();
  }, []);

  const handleNavClick = useCallback((path: string) => {
    setPulsingPath(path);
    if (path === '/recruiter/new') {
      navigate(path, { state: { reset: true } });
    } else {
      navigate(path);
    }
  }, [navigate]);

  useEffect(() => {
    if (!pulsingPath) return;
    const t = setTimeout(() => setPulsingPath(null), 350);
    return () => clearTimeout(t);
  }, [pulsingPath]);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await signOut(auth);
      clearSessionData();
      window.location.href = '/';
    } catch (e) {
      console.warn('Firebase signOut:', e);
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut]);

  const navItem = useCallback((
    path: string,
    label: string,
    iconSvg: React.ReactNode,
    exact?: boolean
  ) => {
    const active = exact ? location.pathname === path : location.pathname.startsWith(path);
    const isPulsing = path === pulsingPath;
    return React.createElement(
      'button',
      {
        type: 'button',
        key: path,
        className: `recruiter-sidebar-nav-item ${active ? 'active' : ''} ${isPulsing ? 'recruiter-sidebar-nav-item--pulse' : ''}`,
        onClick: () => handleNavClick(path),
        title: isSidebarCollapsed ? label : undefined,
      },
      isSidebarCollapsed
        ? React.createElement('span', { className: 'nav-item-icon' }, iconSvg)
        : React.createElement(
          React.Fragment,
          null,
          React.createElement('span', { className: 'nav-item-icon nav-item-icon--expanded' }, iconSvg),
          React.createElement('span', { className: 'nav-item-text' }, label)
        )
    );
  }, [location.pathname, pulsingPath, isSidebarCollapsed, handleNavClick]);

  const accountLabel = useMemo(() => user?.displayName || user?.email || t('sidebar.account'), [user, t]);
  const accountAvatar = useMemo(() => user?.photoURL
    ? React.createElement('img', { src: user.photoURL, alt: '', className: 'recruiter-sidebar-account-avatar' })
    : React.createElement('div', { className: 'recruiter-sidebar-account-initials' }, getInitials(user?.displayName || user?.email || ''))
    , [user]);

  return (
    <div className={`recruiter-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}>
      <div className="recruiter-layout-l-bg">
        <BackgroundParticles
          intensity={0.7}
          particleCount={40}
          colorSet={theme === 'violet' ? 'white' : theme === 'violet-inverted' ? 'violet' : 'default'}
        />
      </div>

      <aside className={`recruiter-sidebar recruiter-sidebar--no-bg ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="recruiter-sidebar-header">
          <button
            type="button"
            className="recruiter-sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={isSidebarCollapsed ? t('layout.expandMenu') : t('layout.collapseMenu')}
            title={isSidebarCollapsed ? t('layout.expandMenu') : t('layout.collapseMenu')}
          >
            {isSidebarCollapsed ? expandIconSvg : collapseIconSvg}
          </button>

          {!isSidebarCollapsed && (
            <div className={`recruiter-sidebar-logo recruiter-sidebar-logo--${theme}`}>
              <LogoInsaite
                size="medium"
                position="relative"
                variant={theme === 'light-pure' ? 'dark' : 'light'}
              />
            </div>
          )}

          {isSidebarCollapsed && (
            <div className={`recruiter-sidebar-logo recruiter-sidebar-logo--${theme}`}>
              <LogoInsaite
                size="small"
                position="relative"
                variant={theme === 'light-pure' ? 'dark' : 'light'}
                aiOnly
              />
            </div>
          )}
        </div>

        <div className="recruiter-sidebar-spacer" />

        <div className="recruiter-sidebar-bottom">
          <nav className="recruiter-sidebar-nav">
            {navItem('/recruiter', t('sidebar.panel'), dashboardIconSvg, true)}
            {navItem('/recruiter/new', t('sidebar.new'), jdUploadIconSvg)}
            {PAYMENTS_ENABLED && !isPremium && navItem('/recruiter/checkout', t('sidebar.premium'), premiumIconSvg)}
            {isPremium && (
              <div className="recruiter-sidebar-pro-badge">
                <span className="badge-icon">⭐</span>
                {!isSidebarCollapsed && <span className="badge-text">PRO</span>}
              </div>
            )}
            {navItem('/recruiter/settings', t('sidebar.settings'), settingsIconSvg)}
            {/* Un administrador entra aquí desde su panel; sin esta salida se
                quedaría dentro de la vista del reclutador sin forma de volver. */}
            {esAdmin && navItem('/admin', t('sidebar.backToAdmin'), adminPanelIconSvg, true)}
          </nav>

          <div className="recruiter-sidebar-footer">
            {user && (
              <div className={`recruiter-sidebar-account ${isSidebarCollapsed ? 'recruiter-sidebar-account--collapsed' : ''}`}>
                <div className="recruiter-sidebar-account-avatar-wrap">{accountAvatar}</div>
                {!isSidebarCollapsed && <span className="recruiter-sidebar-account-label">{accountLabel}</span>}
              </div>
            )}
            <button
              type="button"
              className="recruiter-sidebar-nav-item recruiter-sidebar-logout"
              onClick={() => setShowLogoutModal(true)}
              disabled={isLoggingOut}
              title={isSidebarCollapsed ? t('sidebar.logout') : undefined}
            >
              {isSidebarCollapsed ? (
                <span className="nav-item-icon">{logoutIconSvg}</span>
              ) : (
                <span className="nav-item-text">{isLoggingOut ? t('sidebar.closing') : t('sidebar.logout')}</span>
              )}
            </button>
          </div>
        </div>
      </aside>

      <main className="recruiter-layout-main">
        <ImpersonationBanner />
        <div className="recruiter-breadcrumbs-wrap">
          <Breadcrumbs />
          <div className="recruiter-header-actions">

            <button
              className="recruiter-lang-selector"
              onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              title={lang === 'es' ? t('candidate.switchToEnglish') : t('candidate.switchToSpanish')}
              aria-label={t('candidate.changeLanguage')}
            >
              {globeIconSvg}
            </button>

            <div className="notification-bell-container">
              <button
                className={`notification-bell-btn ${unreadCount > 0 ? 'has-notifications' : ''}`}
                title={t('layout.notifications')}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                {bellIconSvg}
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    <h3>{t('layout.notificationsHeader')}</h3>
                    <button onClick={markAllAsRead}>{t('layout.markAllRead')}</button>
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <div className="notification-empty">{t('layout.noNotifications')}</div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          className={`notification-item ${!n.read ? 'unread' : ''}`}
                          onClick={() => {
                            markAsRead(n.id);
                            if (n.vacanteId) {
                              navigate(`/recruiter?highlight=${n.vacanteId}`);
                            }
                            setShowNotifications(false);
                          }}
                        >
                          <div className="notification-item-icon">
                            {getNotificationIcon(n.type)}
                          </div>
                          <div className="notification-item-content">
                            <p className="notification-title">{n.titleKey ? t(n.titleKey) : n.title}</p>
                            <p className="notification-description">{n.messageKey ? t(n.messageKey) : n.message}</p>
                            <span className="notification-time">{new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="recruiter-layout-content">
          <Outlet />
        </div>
      </main>

      <AppModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title={t('modal.logoutTitle')}
        children={t('modal.logoutMessage')}
        icon={logoutIconSvg}
        onConfirm={() => {
          setShowLogoutModal(false);
          handleLogout();
        }}
        confirmLabel={t('modal.logoutConfirm')}
        confirmVariant="danger"
        showCancel
        cancelLabel={t('modal.cancel')}
      />
      <OnboardingGuide />
    </div>
  );
}
